import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { authService } from '../services/authService';
import { DashboardLayout } from '../components/dashboard/DashboardLayout';
import { ApiKeyManager } from '../components/dashboard/ApiKeyManager';
import { ExtensionManager } from '../components/dashboard/ExtensionManager';
import { TutorialsSection } from '../components/dashboard/TutorialsSection';
import {
    Settings,
    Lock,
    Eye,
    EyeOff,
    Save,
    Sun,
    Moon,
    Monitor,
    Key,
    Trash2,
    AlertTriangle,
    CheckCircle,
    AlertCircle,
    X,
    Package,
    BookOpen
} from 'lucide-react';

export const AccountPage = () => {
    const { user, logout } = useAuth();
    const { darkMode, toggleTheme } = useTheme();
    const navigate = useNavigate();

    // Password change state
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [changingPassword, setChangingPassword] = useState(false);
    const [passwordMessage, setPasswordMessage] = useState(null);
    const [passwordErrors, setPasswordErrors] = useState({});

    // Account deletion state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    // Active tab state
    const [activeTab, setActiveTab] = useState('security'); // 'security', 'preferences', 'integrations', 'tutorials', 'extensions'

    const validatePasswordForm = () => {
        const errors = {};

        if (!passwordForm.currentPassword) {
            errors.currentPassword = 'Current password is required';
        }

        if (!passwordForm.newPassword || passwordForm.newPassword.length < 6) {
            errors.newPassword = 'New password must be at least 6 characters';
        }

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const getPasswordStrength = (password) => {
        if (!password) return { strength: 0, label: '', color: '' };

        let strength = 0;
        if (password.length >= 6) strength++;
        if (password.length >= 10) strength++;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
        if (/\d/.test(password)) strength++;
        if (/[^a-zA-Z0-9]/.test(password)) strength++;

        const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
        const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];

        return {
            strength,
            label: labels[strength - 1] || 'Very Weak',
            color: colors[strength - 1] || 'bg-red-500',
            percentage: (strength / 5) * 100
        };
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();

        if (!validatePasswordForm()) {
            return;
        }

        setChangingPassword(true);
        setPasswordMessage(null);

        try {
            await authService.changePassword(
                passwordForm.currentPassword,
                passwordForm.newPassword
            );

            setPasswordMessage({
                type: 'success',
                text: 'Password changed successfully! Please log in again.'
            });

            // Reset form
            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });

            // Logout and redirect after 2 seconds
            setTimeout(async () => {
                await logout();
                navigate('/login');
            }, 2000);

        } catch (error) {
            console.error('Password change failed:', error);

            if (error.response?.status === 404) {
                setPasswordMessage({
                    type: 'error',
                    text: 'Password change not available yet. Backend endpoint not implemented.'
                });
            } else if (error.response?.status === 401) {
                setPasswordMessage({
                    type: 'error',
                    text: 'Current password is incorrect.'
                });
            } else {
                setPasswordMessage({
                    type: 'error',
                    text: error.response?.data?.detail || 'Failed to change password. Please try again.'
                });
            }
        } finally {
            setChangingPassword(false);
        }
    };

    const handleDeleteAccount = async () => {
        if (!deletePassword) {
            setDeleteError('Please enter your password to confirm');
            return;
        }

        setDeleting(true);
        setDeleteError('');

        try {
            await authService.deleteAccount(deletePassword);

            // Logout and redirect
            await logout();
            navigate('/login');

        } catch (error) {
            console.error('Account deletion failed:', error);

            if (error.response?.status === 404) {
                setDeleteError('Account deletion not available yet. Backend endpoint not implemented.');
            } else if (error.response?.status === 401) {
                setDeleteError('Incorrect password.');
            } else {
                setDeleteError(error.response?.data?.detail || 'Failed to delete account.');
            }
        } finally {
            setDeleting(false);
        }
    };

    const passwordStrength = getPasswordStrength(passwordForm.newPassword);

    return (
        <DashboardLayout>
            <div className="space-y-4 sm:space-y-6 animate-fade-in max-w-4xl mx-auto">
                {/* Header */}
                <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Account</h2>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
                        Manage your account, security, integrations, and get started with tutorials
                    </p>
                </div>

                {/* Tab Navigation */}
                <div className="card p-0 overflow-hidden">
                    {/* Mobile: Dropdown */}
                    <div className="block md:hidden border-b dark:border-gray-700 p-4">
                        <select
                            value={activeTab}
                            onChange={(e) => setActiveTab(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="security">🔒 Security</option>
                            <option value="preferences">⚙️ Preferences</option>
                            <option value="integrations">🔑 Integrations</option>
                            <option value="tutorials">📖 Tutorials</option>
                            <option value="extensions">📦 Extensions</option>
                        </select>
                    </div>

                    {/* Tablet+: Horizontal tabs */}
                    <div className="hidden md:flex border-b dark:border-gray-700 overflow-x-auto scrollbar-hide">
                        <button
                            onClick={() => setActiveTab('security')}
                            className={`flex-shrink-0 min-w-[100px] px-4 lg:px-6 py-4 font-medium text-sm transition-colors ${activeTab === 'security'
                                ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                                }`}
                        >
                            <Lock className="w-4 h-4 inline mr-2" />
                            Security
                        </button>
                        <button
                            onClick={() => setActiveTab('preferences')}
                            className={`flex-shrink-0 min-w-[100px] px-4 lg:px-6 py-4 font-medium text-sm transition-colors ${activeTab === 'preferences'
                                ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                                }`}
                        >
                            <Settings className="w-4 h-4 inline mr-2" />
                            Preferences
                        </button>
                        <button
                            onClick={() => setActiveTab('integrations')}
                            className={`flex-shrink-0 min-w-[100px] px-4 lg:px-6 py-4 font-medium text-sm transition-colors ${activeTab === 'integrations'
                                ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                                }`}
                        >
                            <Key className="w-4 h-4 inline mr-2" />
                            Integrations
                        </button>
                        <button
                            onClick={() => setActiveTab('tutorials')}
                            className={`flex-shrink-0 min-w-[100px] px-4 lg:px-6 py-4 font-medium text-sm transition-colors ${activeTab === 'tutorials'
                                ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                                }`}
                        >
                            <BookOpen className="w-4 h-4 inline mr-2" />
                            Tutorials
                        </button>
                        <button
                            onClick={() => setActiveTab('extensions')}
                            className={`flex-shrink-0 min-w-[100px] px-4 lg:px-6 py-4 font-medium text-sm transition-colors ${activeTab === 'extensions'
                                ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                                }`}
                        >
                            <Package className="w-4 h-4 inline mr-2" />
                            Extensions
                        </button>
                    </div>

                    {/* Tab Content */}
                    <div className="p-4 sm:p-6">
                        {/* Security Tab */}
                        {activeTab === 'security' && (
                            <div className="space-y-6">
                                {/* Password Change Section */}
                                <div>
                                    <h3 className="text-xl font-semibold mb-4">Change Password</h3>

                                    {passwordMessage && (
                                        <div className={`mb-4 p-4 rounded-lg flex items-center gap-3 ${passwordMessage.type === 'success'
                                            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
                                            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
                                            }`}>
                                            {passwordMessage.type === 'success' ? (
                                                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                                            ) : (
                                                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                            )}
                                            <span className="flex-1">{passwordMessage.text}</span>
                                            <button onClick={() => setPasswordMessage(null)}>
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}

                                    <form onSubmit={handlePasswordChange} className="space-y-4">
                                        {/* Current Password */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Current Password
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showPasswords.current ? 'text' : 'password'}
                                                    value={passwordForm.currentPassword}
                                                    onChange={(e) => setPasswordForm(prev => ({
                                                        ...prev,
                                                        currentPassword: e.target.value
                                                    }))}
                                                    className={`w-full px-4 py-2 pr-10 rounded-lg border ${passwordErrors.currentPassword
                                                        ? 'border-red-300 dark:border-red-700'
                                                        : 'border-gray-300 dark:border-gray-600'
                                                        } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPasswords(prev => ({
                                                        ...prev,
                                                        current: !prev.current
                                                    }))}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                                                >
                                                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                            </div>
                                            {passwordErrors.currentPassword && (
                                                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                                    {passwordErrors.currentPassword}
                                                </p>
                                            )}
                                        </div>

                                        {/* New Password */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                New Password
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showPasswords.new ? 'text' : 'password'}
                                                    value={passwordForm.newPassword}
                                                    onChange={(e) => setPasswordForm(prev => ({
                                                        ...prev,
                                                        newPassword: e.target.value
                                                    }))}
                                                    className={`w-full px-4 py-2 pr-10 rounded-lg border ${passwordErrors.newPassword
                                                        ? 'border-red-300 dark:border-red-700'
                                                        : 'border-gray-300 dark:border-gray-600'
                                                        } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPasswords(prev => ({
                                                        ...prev,
                                                        new: !prev.new
                                                    }))}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                                                >
                                                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                            </div>
                                            {passwordErrors.newPassword && (
                                                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                                    {passwordErrors.newPassword}
                                                </p>
                                            )}

                                            {/* Password Strength Indicator */}
                                            {passwordForm.newPassword && (
                                                <div className="mt-2">
                                                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full ${passwordStrength.color} transition-all duration-300`}
                                                            style={{ width: `${passwordStrength.percentage}%` }}
                                                        />
                                                    </div>
                                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                                        Strength: {passwordStrength.label}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Confirm Password */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Confirm New Password
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showPasswords.confirm ? 'text' : 'password'}
                                                    value={passwordForm.confirmPassword}
                                                    onChange={(e) => setPasswordForm(prev => ({
                                                        ...prev,
                                                        confirmPassword: e.target.value
                                                    }))}
                                                    className={`w-full px-4 py-2 pr-10 rounded-lg border ${passwordErrors.confirmPassword
                                                        ? 'border-red-300 dark:border-red-700'
                                                        : 'border-gray-300 dark:border-gray-600'
                                                        } bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPasswords(prev => ({
                                                        ...prev,
                                                        confirm: !prev.confirm
                                                    }))}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                                                >
                                                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                            </div>
                                            {passwordErrors.confirmPassword && (
                                                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                                    {passwordErrors.confirmPassword}
                                                </p>
                                            )}
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={changingPassword}
                                            className="px-6 py-2 text-white rounded-lg transition-all shadow-glow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                            style={{ background: 'linear-gradient(135deg, #06B6D4, #3B82F6)' }}
                                        >
                                            <Save className="w-4 h-4" />
                                            {changingPassword ? 'Changing...' : 'Change Password'}
                                        </button>
                                    </form>
                                </div>

                                {/* Danger Zone */}
                                <div className="pt-6 border-t dark:border-gray-700">
                                    <h3 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-4">
                                        Danger Zone
                                    </h3>
                                    <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                                        <div className="flex items-start gap-3">
                                            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-red-900 dark:text-red-200">
                                                    Delete Account
                                                </h4>
                                                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                                                    Once you delete your account, there is no going back. This action is permanent.
                                                </p>
                                                <button
                                                    onClick={() => setShowDeleteModal(true)}
                                                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                    Delete Account
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Preferences Tab */}
                        {activeTab === 'preferences' && (
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-xl font-semibold mb-4">Appearance</h3>

                                    <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                            Theme
                                        </label>

                                        <div className="flex gap-3">
                                            <button
                                                onClick={toggleTheme}
                                                className={`flex-1 p-4 rounded-lg border-2 transition-all ${!darkMode
                                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                                    : 'border-gray-300 dark:border-gray-600 hover:border-blue-300'
                                                    }`}
                                            >
                                                <Sun className="w-6 h-6 mx-auto mb-2 text-yellow-500" />
                                                <div className="text-sm font-medium">Light</div>
                                            </button>

                                            <button
                                                onClick={toggleTheme}
                                                className={`flex-1 p-4 rounded-lg border-2 transition-all ${darkMode
                                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                                    : 'border-gray-300 dark:border-gray-600 hover:border-blue-300'
                                                    }`}
                                            >
                                                <Moon className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                                                <div className="text-sm font-medium">Dark</div>
                                            </button>
                                        </div>

                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-3">
                                            Current theme: <span className="font-semibold">{darkMode ? 'Dark' : 'Light'}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Integrations Tab */}
                        {activeTab === 'integrations' && (
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-xl font-semibold mb-4">API Key Management</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                        Bring your own Gemini API key to use the service with your own quota.
                                    </p>
                                    <ApiKeyManager />
                                </div>
                            </div>
                        )}

                        {/* Tutorials Tab */}
                        {activeTab === 'tutorials' && (
                            <div className="space-y-6">
                                <TutorialsSection />
                            </div>
                        )}

                        {/* Extensions Tab */}
                        {activeTab === 'extensions' && (
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-xl font-semibold mb-4">Extension Distribution</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                        Manage extension releases and cache settings.
                                    </p>
                                    <ExtensionManager />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Account Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-sm sm:max-w-md p-4 sm:p-6 shadow-xl">
                        <h3 className="text-lg sm:text-xl font-bold text-red-600 dark:text-red-400 mb-3 sm:mb-4">
                            Confirm Account Deletion
                        </h3>
                        <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-3 sm:mb-4">
                            This action cannot be undone. All your data will be permanently deleted.
                        </p>

                        {deleteError && (
                            <div className="mb-3 sm:mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 text-sm">
                                {deleteError}
                            </div>
                        )}

                        <div className="mb-3 sm:mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Enter your password to confirm
                            </label>
                            <input
                                type="password"
                                value={deletePassword}
                                onChange={(e) => setDeletePassword(e.target.value)}
                                className="w-full px-4 py-3 min-h-[48px] rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                placeholder="Enter password"
                            />
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={handleDeleteAccount}
                                disabled={deleting}
                                className="w-full sm:flex-1 px-4 py-3 min-h-[48px] bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {deleting ? 'Deleting...' : 'Delete My Account'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeletePassword('');
                                    setDeleteError('');
                                }}
                                disabled={deleting}
                                className="w-full sm:w-auto px-4 py-3 min-h-[48px] bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};
